/**
 * Turning what is on screen into what the printer prints.
 *
 * One function per document, all producing the same block shape, so the thermal
 * layout lives in the driver rather than being reinvented in four components.
 */

import type { FormatContext } from '@/lib/format';
import { formatDateTime, formatMoney, formatQuantity } from '@/lib/format';
import type { PrintBlock, PrintDocument } from '@/lib/printing';
import type { AccountTransaction, GoodsReceipt, PurchaseOrder, Sale } from '@/api/endpoints';

interface Business {
  name?: string;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  phone?: string | null;
  email?: string | null;
  taxNumber?: string | null;
}

/**
 * The lines every receipt starts with.
 *
 * Name, then how to find the business, then how to reach it — a customer who
 * comes back with a receipt is usually looking for one of those two. All of it
 * comes from the business settings, so it is filled in once rather than
 * carried on a rubber stamp.
 */
function heading(business: Business, taxLabel: string, title: string): PrintBlock[] {
  const address = [business.addressLine1, business.addressLine2, business.city]
    .filter(Boolean)
    .join(', ');

  const contact = [business.phone, business.email].filter(Boolean).join(' · ');

  const blocks: PrintBlock[] = [{ type: 'center', text: business.name ?? '' }];
  if (address) blocks.push({ type: 'center', text: address });
  if (contact) blocks.push({ type: 'center', text: contact });
  if (business.taxNumber) {
    blocks.push({ type: 'center', text: `${taxLabel}: ${business.taxNumber}` });
  }

  blocks.push({ type: 'rule', char: '=' });
  blocks.push({ type: 'center', text: title });
  blocks.push({ type: 'rule', char: '=' });
  return blocks;
}

function footer(note: string | null | undefined, format: FormatContext): PrintBlock[] {
  return [
    { type: 'rule' },
    { type: 'center', text: note || 'Thank you for your business.' },
    { type: 'center', text: `Printed ${formatDateTime(new Date().toISOString(), format)}` },
  ];
}

export function saleDocument(
  sale: Sale,
  business: Business,
  format: FormatContext,
  options: { taxLabel?: string; footerNote?: string | null } = {},
): PrintDocument {
  const blocks: PrintBlock[] = heading(business, options.taxLabel ?? 'Tax ID', 'SALES RECEIPT');

  blocks.push(
    { type: 'pair', label: 'Receipt', value: sale.number },
    { type: 'pair', label: 'Date', value: formatDateTime(sale.issuedAt ?? sale.createdAt, format) },
  );
  if (sale.branch) blocks.push({ type: 'pair', label: 'Branch', value: sale.branch.name });
  blocks.push({ type: 'pair', label: 'Customer', value: sale.customer?.name ?? 'Walk-in' });
  blocks.push({ type: 'rule' });

  for (const item of sale.items ?? []) {
    blocks.push({
      type: 'item',
      label: item.description || item.variant.product.name,
      sku: item.variant.sku,
      quantity: formatQuantity(item.quantity, format),
      price: formatMoney(item.unitPrice, format),
      total: formatMoney(item.lineTotal, format),
    });
  }

  blocks.push({ type: 'rule' });
  blocks.push({ type: 'pair', label: 'Subtotal', value: formatMoney(sale.subtotal, format) });

  if (Number(sale.discountAmount) > 0) {
    blocks.push({ type: 'pair', label: 'Discount', value: `-${formatMoney(sale.discountAmount, format)}` });
  }
  if (Number(sale.taxAmount) > 0) {
    blocks.push({ type: 'pair', label: 'Tax', value: formatMoney(sale.taxAmount, format) });
  }

  blocks.push({ type: 'big', label: 'TOTAL', text: formatMoney(sale.total, format) });

  for (const tender of sale.payments ?? []) {
    blocks.push({
      type: 'pair',
      label: tender.payment.method.name,
      value: formatMoney(tender.amount, format),
    });
  }

  if (Number(sale.dueAmount) > 0) {
    blocks.push({ type: 'big', label: 'BALANCE DUE', text: formatMoney(sale.dueAmount, format) });
  }

  blocks.push(...footer(options.footerNote, format));
  return { blocks };
}

export function cashMovementDocument(
  entry: AccountTransaction,
  business: Business,
  format: FormatContext,
  titles: Record<string, string>,
): PrintDocument {
  const inbound = entry.direction === 'IN';
  const blocks: PrintBlock[] = heading(
    business,
    'Tax ID',
    titles[entry.type] ?? entry.type.replaceAll('_', ' '),
  );

  blocks.push(
    { type: 'pair', label: 'Account', value: entry.account.name },
    { type: 'pair', label: 'When', value: formatDateTime(entry.occurredAt, format) },
    { type: 'pair', label: 'Source', value: entry.referenceType.toLowerCase().replaceAll('_', ' ') },
  );
  if (entry.referenceNumber) {
    blocks.push({ type: 'pair', label: 'Ref', value: entry.referenceNumber });
  }

  blocks.push({
    type: 'big',
    label: inbound ? 'RECEIVED' : 'PAID OUT',
    text: formatMoney(entry.amount, format),
  });

  blocks.push(
    { type: 'pair', label: 'Balance before', value: formatMoney(entry.balanceBefore, format) },
    { type: 'pair', label: 'Balance after', value: formatMoney(entry.balanceAfter, format) },
  );

  if (entry.notes) blocks.push({ type: 'rule' }, { type: 'text', text: entry.notes });

  blocks.push({
    type: 'pair',
    label: 'Recorded by',
    value: entry.createdBy ? `${entry.createdBy.firstName} ${entry.createdBy.lastName}` : 'the system',
  });

  blocks.push(...footer('Keep this slip.', format));
  return { blocks };
}

export function purchaseDocument(
  document: PurchaseOrder | GoodsReceipt,
  isReceipt: boolean,
  business: Business,
  format: FormatContext,
): PrintDocument {
  const blocks: PrintBlock[] = heading(
    business,
    'Tax ID',
    isReceipt ? 'GOODS RECEIVED NOTE' : 'PURCHASE ORDER',
  );

  blocks.push(
    { type: 'pair', label: 'Number', value: document.number },
    { type: 'pair', label: 'Supplier', value: document.supplier.name },
    { type: 'pair', label: 'Status', value: document.status.toLowerCase().replaceAll('_', ' ') },
  );

  if (isReceipt) {
    const receipt = document as GoodsReceipt;
    blocks.push(
      { type: 'pair', label: 'Received', value: formatDateTime(receipt.receivedAt, format) },
      { type: 'pair', label: 'Warehouse', value: receipt.warehouse.name },
    );
    if (receipt.order) {
      blocks.push({ type: 'pair', label: 'Against order', value: receipt.order.number });
    }
  } else {
    blocks.push({
      type: 'pair',
      label: 'Raised',
      value: formatDateTime((document as PurchaseOrder).createdAt, format),
    });
  }

  blocks.push({ type: 'rule' });

  if (isReceipt) {
    for (const item of (document as GoodsReceipt).items ?? []) {
      blocks.push({
        type: 'item',
        label: item.variant.product.name,
        sku: item.variant.sku,
        quantity: formatQuantity(item.quantity, format),
        price: formatMoney(item.unitCost, format),
        total: formatMoney(item.lineTotal, format),
      });
      if (Number(item.rejectedQty) > 0) {
        blocks.push({
          type: 'text',
          text: `${formatQuantity(item.rejectedQty, format)} rejected`,
        });
      }
    }
  } else {
    for (const item of (document as PurchaseOrder).items ?? []) {
      blocks.push({
        type: 'item',
        label: item.description,
        quantity: formatQuantity(item.quantity, format),
        price: formatMoney(item.unitCost, format),
        total: formatMoney(item.lineTotal, format),
      });
    }
  }

  blocks.push({
    type: 'big',
    label: isReceipt ? 'RECEIVED' : 'ORDERED',
    text: formatMoney(
      isReceipt ? (document as GoodsReceipt).totalCost : (document as PurchaseOrder).total,
      format,
    ),
  });

  // These are signed at a gate, so there is somewhere to sign.
  blocks.push({ type: 'signature', label: 'Received by' }, { type: 'signature' });
  blocks.push(...footer(null, format));
  return { blocks };
}

/**
 * A property sale receipt, from the payload the API builds for it.
 *
 * The driver can lay that payload out itself, but a phone printing over
 * Bluetooth needs finished bytes, and those are rendered from blocks. So the
 * payload is turned into the same blocks as everything else and every route
 * prints the same receipt.
 */
export function propertyReceiptDocument(payload: Record<string, any>): PrintDocument {
  const business = payload.business ?? {};
  const sale = payload.sale ?? {};
  const property = payload.property ?? {};
  const buyer = payload.buyer ?? {};
  const payment = payload.payment as Record<string, string> | null | undefined;
  const history = (payload.history ?? []) as { index: number; amount: string; receivedAt: string }[];
  const currency = payload.currency ?? {};

  /** The server has already rounded; this only puts the symbol on. */
  const money = (value?: string | null): string => {
    if (value === undefined || value === null) return '';
    const amount = Number(value);
    const body = Number.isNaN(amount)
      ? String(value)
      : amount.toLocaleString(undefined, {
          minimumFractionDigits: currency.decimals ?? 0,
          maximumFractionDigits: currency.decimals ?? 0,
        });
    return currency.position === 'AFTER' ? `${body}${currency.symbol ?? ''}` : `${currency.symbol ?? ''}${body}`;
  };

  const blocks: PrintBlock[] = [{ type: 'center', text: business.name ?? '' }];
  if (business.address) blocks.push({ type: 'center', text: business.address });
  const contact = [business.phone, business.email].filter(Boolean).join(' · ');
  if (contact) blocks.push({ type: 'center', text: contact });
  if (business.taxNumber) {
    blocks.push({ type: 'center', text: `${business.taxLabel ?? 'Tax ID'}: ${business.taxNumber}` });
  }

  blocks.push({ type: 'rule', char: '=' });
  blocks.push({ type: 'center', text: 'PROPERTY SALE RECEIPT' });
  blocks.push({ type: 'rule', char: '=' });

  const line = (label: string, value?: string | null): void => {
    if (value) blocks.push({ type: 'pair', label, value: String(value) });
  };

  line('Receipt', sale.number);
  line('Buyer', buyer.name);
  line('Phone', buyer.phone);

  blocks.push({ type: 'rule' });
  blocks.push({ type: 'text', text: `${property.reference ?? ''} ${property.name ?? ''}`.trim() });
  line('Title no.', property.titleNumber);
  line('Plot', property.plotNumber);
  line('Block', property.blockNumber);
  line('Area', property.area);
  line('Location', property.location);
  line('District', property.district);

  if (payment) {
    blocks.push({ type: 'rule' });
    line('Paid now', money(payment.amount));
    line('Method', payment.method);
    line('Ref', payment.reference);
  }

  blocks.push({ type: 'rule' });
  line('Price', money(sale.price));
  line('Paid to date', money(sale.paid));
  blocks.push({ type: 'big', label: 'BALANCE DUE', text: money(sale.balance) });

  if (history.length > 1) {
    blocks.push({ type: 'rule' });
    blocks.push({ type: 'center', text: 'Payments so far' });
    for (const entry of history) {
      blocks.push({
        type: 'pair',
        label: `${entry.index}. ${entry.receivedAt.slice(0, 10)}`,
        value: money(entry.amount),
      });
    }
  }

  blocks.push({ type: 'rule' });
  blocks.push({ type: 'center', text: business.footer || 'Thank you.' });
  blocks.push({ type: 'center', text: `Printed ${new Date().toLocaleString()}` });

  return { template: payload.template ?? 'THERMAL_58', blocks };
}
